import React from 'react';

import { cn } from '@/lib/utils';
import { SELECTED } from './selection';

export interface SegmentedChoiceOption<T> {
    value: T;
    label: string;
    /**
     * One line about what this option does, shown under the row while it is
     * the chosen one.
     *
     * Worth writing. Two words on a pill say which of these you picked; they
     * do not say what either one is, and options whose names sound similar
     * ("With environment" / "Product only") are exactly where that costs
     * someone a generation to find out.
     */
    hint?: string;
    /** A small icon before the label — for choices worth spotting at a glance. */
    icon?: React.ReactNode;
}

interface SegmentedChoiceProps<T> {
    value: T;
    options: SegmentedChoiceOption<T>[];
    onChange: (value: T) => void;
    /** Full-width row of equal pills. Set false to size them to their labels. */
    stretch?: boolean;
}

/**
 * A row of pills where exactly one is chosen, with a line explaining it.
 *
 * Extracted the second time a card needed one — the shot-type toggle and the
 * From Reference route chooser are the same control, and the second would
 * otherwise have been a copy of the first.
 */
export function SegmentedChoice<T extends string | number | boolean>({
    value,
    options,
    onChange,
    stretch = true,
}: SegmentedChoiceProps<T>) {
    const chosen = options.find((o) => Object.is(o.value, value));

    return (
        <div>
            <div className="flex gap-1.5">
                {options.map((o) => {
                    const active = Object.is(o.value, value);
                    return (
                        <button
                            key={String(o.value)}
                            type="button"
                            aria-pressed={active}
                            onClick={() => onChange(o.value)}
                            className={cn(
                                'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border py-1 text-xs transition-colors hover:bg-accent',
                                o.icon && 'py-1.5',
                                stretch ? 'flex-1' : 'px-3',
                                active
                                    ? `${SELECTED} font-medium text-foreground`
                                    : 'border-input bg-background font-normal text-muted-foreground',
                            )}
                        >
                            {o.icon}
                            {o.label}
                        </button>
                    );
                })}
            </div>
            {chosen?.hint && (
                <p className="mt-1 text-[11px] text-muted-foreground">{chosen.hint}</p>
            )}
        </div>
    );
}
